import * as XLSX from "xlsx";
import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  ShadingType,
} from "docx";
import { calcTotals, fmtEUR, fmtDate } from "./storage";

function descargarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function clientLines(c) {
  return [
    c?.nombre || "—",
    c?.nif ? `NIF/CIF: ${c.nif}` : null,
    c?.direccion || null,
    c?.telefono ? `Tel: ${c.telefono}` : null,
    c?.email || null,
  ].filter(Boolean);
}

function nombreArchivo(budget, ext) {
  const cliente = (budget?.cliente?.nombre || "").replace(/[^\w\d-]+/g, "_");
  const piezas = ["Presupuesto", budget?.id, cliente].filter(Boolean);
  return `${piezas.join("_")}.${ext}`;
}

export function exportBudgetXLSX(budget) {
  const totals = calcTotals(budget.partidas);
  const cliente = budget.cliente ?? {};
  const tipoLabel = budget.tipo === "rapido" ? "Rápido" : "Completo";

  const aoa = [];
  aoa.push(["Reformas Express"]);
  aoa.push(["Presupuesto profesional de reforma"]);
  aoa.push([]);
  aoa.push(["Presupuesto Nº", budget.id]);
  aoa.push(["Fecha", fmtDate(budget.fecha)]);
  aoa.push(["Tipo", tipoLabel]);
  aoa.push(["Estado", budget.estado === "final" ? "Final" : "Borrador"]);
  aoa.push([]);
  aoa.push(["CLIENTE"]);
  for (const l of clientLines(cliente)) aoa.push([l]);
  aoa.push([]);
  aoa.push(["#", "Descripción", "Unidad", "Cantidad", "P. Unit.", "Subtotal"]);

  const headerRowIndex = aoa.length - 1;
  const partidas = budget.partidas ?? [];
  partidas.forEach((p, i) => {
    const cantidad = Number(p.cantidad) || 0;
    const precio = Number(p.precio_unitario) || 0;
    const subtotal = cantidad * precio;
    const concepto = (p.concepto ?? p.descripcion ?? "") +
      (p.is_estimation ? " (Est. IA)" : "");
    aoa.push([i + 1, concepto, p.unidad ?? "ud", cantidad, precio, subtotal]);
  });
  const lastDataRow = aoa.length - 1;

  aoa.push([]);
  aoa.push(["", "", "", "", "Base imponible", totals.base]);
  aoa.push(["", "", "", "", "IVA (21%)", totals.iva]);
  aoa.push(["", "", "", "", "TOTAL", totals.total]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!cols"] = [
    { wch: 5 },
    { wch: 60 },
    { wch: 10 },
    { wch: 10 },
    { wch: 14 },
    { wch: 14 },
  ];
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
  ];

  const fmtMoney = '#,##0.00 "€"';
  for (let r = headerRowIndex + 1; r <= lastDataRow; r++) {
    const cellPrecio = XLSX.utils.encode_cell({ r, c: 4 });
    const cellSubtotal = XLSX.utils.encode_cell({ r, c: 5 });
    if (ws[cellPrecio]) ws[cellPrecio].z = fmtMoney;
    if (ws[cellSubtotal]) ws[cellSubtotal].z = fmtMoney;
  }
  for (let r = aoa.length - 3; r < aoa.length; r++) {
    const cell = XLSX.utils.encode_cell({ r, c: 5 });
    if (ws[cell]) ws[cell].z = fmtMoney;
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Presupuesto");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  descargarBlob(blob, nombreArchivo(budget, "xlsx"));
}

const COLOR_AZUL = "2563EB";
const COLOR_SLATE = "0F172A";
const COLOR_MUTED = "475569";
const COLOR_BORDER = "E2E8F0";

function borderAll(color = COLOR_BORDER, size = 4) {
  const b = { style: BorderStyle.SINGLE, size, color };
  return { top: b, bottom: b, left: b, right: b };
}

function headerCell(text, opts = {}) {
  return new TableCell({
    width: opts.width
      ? { size: opts.width, type: WidthType.PERCENTAGE }
      : undefined,
    shading: {
      type: ShadingType.CLEAR,
      color: "auto",
      fill: COLOR_AZUL,
    },
    borders: borderAll(COLOR_AZUL),
    children: [
      new Paragraph({
        alignment: opts.align ?? AlignmentType.LEFT,
        children: [
          new TextRun({
            text,
            bold: true,
            color: "FFFFFF",
            size: 20,
          }),
        ],
      }),
    ],
  });
}

function bodyCell(text, opts = {}) {
  return new TableCell({
    width: opts.width
      ? { size: opts.width, type: WidthType.PERCENTAGE }
      : undefined,
    borders: borderAll(),
    children: [
      new Paragraph({
        alignment: opts.align ?? AlignmentType.LEFT,
        children: [
          new TextRun({
            text,
            size: 20,
            color: COLOR_SLATE,
            bold: !!opts.bold,
          }),
        ],
      }),
    ],
  });
}

function totalRow(label, value, opts = {}) {
  return new TableRow({
    children: [
      new TableCell({
        width: { size: 75, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          bottom: opts.bigBorder
            ? { style: BorderStyle.SINGLE, size: 12, color: COLOR_AZUL }
            : { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        },
        children: [
          new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [
              new TextRun({
                text: label,
                bold: !!opts.strong,
                size: opts.strong ? 26 : 20,
                color: opts.strong ? COLOR_AZUL : COLOR_SLATE,
              }),
            ],
          }),
        ],
      }),
      new TableCell({
        width: { size: 25, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          bottom: opts.bigBorder
            ? { style: BorderStyle.SINGLE, size: 12, color: COLOR_AZUL }
            : { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        },
        children: [
          new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [
              new TextRun({
                text: value,
                bold: !!opts.strong,
                size: opts.strong ? 26 : 20,
                color: opts.strong ? COLOR_AZUL : COLOR_SLATE,
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

export async function exportBudgetDOCX(budget) {
  const totals = calcTotals(budget.partidas);
  const cliente = budget.cliente ?? {};
  const tipoLabel = budget.tipo === "rapido" ? "Rápido" : "Completo";
  const partidas = budget.partidas ?? [];

  const headerTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 60, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Reformas Express",
                    bold: true,
                    size: 44,
                    color: COLOR_AZUL,
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Presupuesto profesional de reforma",
                    color: COLOR_MUTED,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 40, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: "Presupuesto Nº: ", bold: true, size: 20 }),
                  new TextRun({ text: budget.id, size: 20 }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: "Fecha: ", bold: true, size: 20 }),
                  new TextRun({ text: fmtDate(budget.fecha), size: 20 }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: "Tipo: ", bold: true, size: 20 }),
                  new TextRun({ text: tipoLabel, size: 20 }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: "Estado: ", bold: true, size: 20 }),
                  new TextRun({
                    text: budget.estado === "final" ? "Final" : "Borrador",
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });

  const clienteHeading = new Paragraph({
    spacing: { before: 320, after: 80 },
    children: [
      new TextRun({
        text: "CLIENTE",
        bold: true,
        size: 18,
        color: COLOR_MUTED,
      }),
    ],
  });
  const clienteLines = clientLines(cliente).map(
    (l) =>
      new Paragraph({
        children: [new TextRun({ text: l, size: 20, color: COLOR_SLATE })],
      }),
  );

  const partidasHeaderRow = new TableRow({
    tableHeader: true,
    children: [
      headerCell("#", { width: 6, align: AlignmentType.RIGHT }),
      headerCell("Descripción", { width: 50 }),
      headerCell("Ud", { width: 8, align: AlignmentType.CENTER }),
      headerCell("Cant.", { width: 10, align: AlignmentType.RIGHT }),
      headerCell("P. Unit.", { width: 12, align: AlignmentType.RIGHT }),
      headerCell("Subtotal", { width: 14, align: AlignmentType.RIGHT }),
    ],
  });
  const partidaRows = partidas.map((p, i) => {
    const cantidad = Number(p.cantidad) || 0;
    const precio = Number(p.precio_unitario) || 0;
    const subtotal = cantidad * precio;
    const concepto =
      (p.concepto ?? p.descripcion ?? "") +
      (p.is_estimation ? "  (Est. IA)" : "");
    return new TableRow({
      children: [
        bodyCell(String(i + 1), { align: AlignmentType.RIGHT }),
        bodyCell(concepto),
        bodyCell(p.unidad ?? "ud", { align: AlignmentType.CENTER }),
        bodyCell(cantidad.toLocaleString("es-ES"), {
          align: AlignmentType.RIGHT,
        }),
        bodyCell(fmtEUR(precio), { align: AlignmentType.RIGHT }),
        bodyCell(fmtEUR(subtotal), { align: AlignmentType.RIGHT, bold: true }),
      ],
    });
  });

  const tablaPartidas = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows:
      partidaRows.length > 0
        ? [partidasHeaderRow, ...partidaRows]
        : [
            partidasHeaderRow,
            new TableRow({
              children: [
                new TableCell({
                  borders: borderAll(),
                  columnSpan: 6,
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new TextRun({
                          text: "Sin partidas",
                          color: COLOR_MUTED,
                          size: 20,
                          italics: true,
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
  });

  const tablaTotales = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    },
    rows: [
      totalRow("Base imponible", fmtEUR(totals.base)),
      totalRow("IVA (21%)", fmtEUR(totals.iva), { bigBorder: true }),
      totalRow("TOTAL", fmtEUR(totals.total), { strong: true }),
    ],
  });

  const footer = new Paragraph({
    spacing: { before: 600 },
    children: [
      new TextRun({
        text: "Presupuesto generado con asistente IA. Revise siempre las partidas, cantidades y precios antes de confirmar.",
        italics: true,
        color: COLOR_MUTED,
        size: 16,
      }),
    ],
  });

  const doc = new Document({
    creator: "Reformas Express",
    title: `Presupuesto ${budget.id}`,
    sections: [
      {
        properties: {
          page: {
            margin: { top: 720, right: 720, bottom: 720, left: 720 },
          },
        },
        children: [
          headerTable,
          clienteHeading,
          ...clienteLines,
          new Paragraph({ spacing: { before: 120 }, children: [] }),
          tablaPartidas,
          new Paragraph({ spacing: { before: 120 }, children: [] }),
          tablaTotales,
          footer,
        ],
      },
    ],
    styles: {
      default: {
        document: {
          run: {
            font: "Calibri",
            size: 20,
          },
        },
      },
    },
  });

  const blob = await Packer.toBlob(doc);
  descargarBlob(blob, nombreArchivo(budget, "docx"));
}
